/**
 * Fetches the file URL from the API using the file ID
 * @param fileId - The file ID to fetch
 * @param accessToken - The access token for authentication
 * @returns Promise that resolves to the file URL or null if fetch fails
 */
export async function fetchFileUrl(
  fileId: number,
  accessToken: string
): Promise<string | null> {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
      console.warn("REACT_APP_API_BASE_URL is not defined");
      return null;
    }

    const endpoint = `${API_BASE_URL}/api/files/files/get/?file_id=${fileId}`;

    const { authenticatedFetch } = await import("../features/auth/apiInterceptor");
    const response = await authenticatedFetch(endpoint, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const responseBody = await response.json().catch(() => null);

    if (!response.ok || !responseBody?.data?.file) {
      return null;
    }

    return responseBody.data.file as string;
  } catch (error) {
    console.error("Failed to fetch file URL:", error);
    return null;
  }
}

/**
 * Utility function to get the profile picture URL
 * @param profilePicture - Can be a number (ID), string (URL), or null
 * @param accessToken - The access token for authentication (required if profilePicture is a number)
 * @param fallbackPath - Fallback image path when profilePicture is null (default: "/fallback-image")
 * @returns Promise that resolves to the image URL or fallback path
 */
export async function getProfilePictureUrl(
  profilePicture: number | string | null | undefined,
  accessToken?: string,
  fallbackPath: string = "/assets/images/Avatar2.jpg",
  profilePictureUrl?: string | null
): Promise<string> {
  // Prefer explicit URL from API response when present.
  if (typeof profilePictureUrl === "string" && profilePictureUrl.trim() !== "") {
    return profilePictureUrl;
  }

  // If profilePicture is null or undefined, return fallback
  if (profilePicture === null || profilePicture === undefined) {
    return fallbackPath;
  }

  // If profilePicture is already a string (URL), return it as is
  if (typeof profilePicture === "string") {
    // Check if it's already a full URL (starts with http:// or https://)
    if (profilePicture.startsWith("http://") || profilePicture.startsWith("https://")) {
      return profilePicture;
    }
    // If it's a relative path, return it
    return profilePicture;
  }

  // If profilePicture is a number (ID), fetch the file URL from API
  if (typeof profilePicture === "number") {
    if (!accessToken) {
      console.warn("Access token is required to fetch file URL");
      return fallbackPath;
    }

    const fileUrl = await fetchFileUrl(profilePicture, accessToken);
    return fileUrl || fallbackPath;
  }

  // Fallback for any other case
  return fallbackPath;
}

